import { secaoDoSlug } from "../../pages/ConfigPanel/configSections";
import { rotuloDoProduto } from "../Automacoes/produtos";

/**
 * Trilha da barra global (GEN-3497).
 *
 * Resolve QUAIS degraus a rota atual tem. Quem desenha é o `Breadcrumb` compartilhado, e
 * quem mostra é o `PageHeader` de cada página (na r6 a trilha saiu da barra global e desceu
 * para dentro da página, junto do título).
 *
 * O último degrau é também o TÍTULO padrão da tela — é por isso que os rótulos aqui são,
 * letra por letra, os mesmos da sidebar: dois nomes para o mesmo lugar é como se perde a
 * confiança na navegação.
 */

export interface ITrailStep {
  label: string;
  /** Rota para onde o degrau leva. Ausente = é o degrau atual, não clicável. */
  path?: string;
}

interface IModule {
  /** Prefixo da rota. A ordem da lista importa: o mais específico vem primeiro. */
  match: (pathname: string) => boolean;
  label: string;
  /** Para onde o degrau do módulo leva quando existe um degrau depois dele. */
  home: string;
  /** Segundo segmento da URL → rótulo da subpágina. */
  children?: Record<string, string>;
}

const MODULES: IModule[] = [
  { match: (p) => p === "/", label: "Dashboard", home: "/" },
  { match: (p) => p.startsWith("/notificacoes"), label: "Notificações", home: "/notificacoes" },
  { match: (p) => p.startsWith("/historico"), label: "Meu histórico", home: "/historico" },
  { match: (p) => p.startsWith("/tarefas"), label: "Minhas tarefas", home: "/tarefas" },
  {
    match: (p) => p.startsWith("/kanban-v2"),
    label: "Kanban 2.0",
    home: "/kanban-v2/quadros",
    children: { quadros: "Quadros", lotes: "Lotes", dashboard: "Dashboard", configuracoes: "Configurações" },
  },
  { match: (p) => p.startsWith("/kanban"), label: "Tax Kanban", home: "/kanban" },
  {
    match: (p) => p.startsWith("/iss"),
    label: "Taxly ISS",
    home: "/iss",
    children: { lotes: "Lotes", cockpit: "Cockpit", dashboard: "Dashboard" },
  },
  { match: (p) => p.startsWith("/certidoes"), label: "Gerenciador de Certidões", home: "/certidoes" },
  { match: (p) => p.startsWith("/caixa-postal"), label: "Caixa Postal", home: "/caixa-postal" },
  {
    match: (p) => p.startsWith("/conta-corrente"),
    label: "Conta Corrente",
    home: "/conta-corrente",
    children: { "situacao-fiscal": "Situação Fiscal", "cadin-federal": "CADIN Federal" },
  },
  { match: (p) => p.startsWith("/consultas-em-lote"), label: "Consultas em Lote", home: "/consultas-em-lote" },
  // Automações virou módulo próprio na sidebar (vindo da development durante esta task); o mapa
  // aqui espelha o menu, então ela entra também.
  { match: (p) => p.startsWith("/automacoes"), label: "Automações", home: "/automacoes" },
  { match: (p) => p.startsWith("/base-cadastral"), label: "Base Cadastral", home: "/base-cadastral" },
  { match: (p) => p.startsWith("/empresas"), label: "Empresas", home: "/empresas" },
  { match: (p) => p.startsWith("/pessoa-fisica"), label: "Pessoas Físicas", home: "/pessoa-fisica" },
  { match: (p) => p.startsWith("/usuarios"), label: "Usuários", home: "/usuarios" },
  { match: (p) => p.startsWith("/credenciais"), label: "Credenciais", home: "/credenciais" },
  { match: (p) => p.startsWith("/configuracoes"), label: "Configurações", home: "/configuracoes" },
];

/** Rótulos de jurisdição — os mesmos que a sidebar usa no submenu de certidões. */
const JURISDICTIONS: Record<string, string> = {
  federal: "Federal",
  state: "Estadual",
  municipal: "Municipal",
};

/** Seções de Configurações — os mesmos rótulos do menu lateral daquela tela. */
const CONFIG_SECTIONS: Record<string, string> = {
  account: "Minha Conta",
  changePassword: "Trocar senha",
  display: "Exibição",
  notification: "Notificações",
  certificate: "Certidões",
  dueTerm: "Regras de vencimento",
  taxCurrentAccount: "Conta Corrente",
  DTE: "Caixa Postal",
  taxKanban: "Tax Kanban",
  consultas: "Consultas",
  files: "Arquivos",
  consumption: "Consumo do contrato",
  baseCadastral: "Base Cadastral",
  certificateCustomField: "Campos personalizados",
  taxKanbanCustomField: "Campos personalizados",
};

/**
 * GEN-4114 — subpáginas do grupo "Portal de autosserviço" de Configurações. A trilha ganha o degrau do grupo
 * ("Configurações / Portal de autosserviço / Pedidos"), com os mesmos rótulos do menu lateral.
 */
const PORTAL_SECTIONS: Record<string, string> = {
  portalRequests: "Pedidos",
  portal: "Acessos",
  portalForm: "Formulário do pedido",
  portalBranding: "Aparência",
  portalIntegrations: "Integrações",
  portalTrail: "Trilha",
};

/** Abas de tipo da lista de Automações (`?tipo=`) — GEN-4080. Mesmos nomes das abas da lib. */
const TIPOS_DE_AUTOMACAO: Record<string, string> = {
  alertas: "Alertas",
  agendamentos: "Agendamentos",
  acoes: "Ações",
};

/**
 * Trilha de Automações: "Automações", "Automações / Alertas", "Automações / Certidões". Com tipo E
 * módulo (ex.: `?tipo=acoes&modulo=kanban2`, a antiga tela de automações do Kanban), três degraus:
 * "Automações / Ações / Kanban". Valor desconhecido na URL não vira degrau — slug cru na tela é pior
 * que um degrau a menos (lição da GEN-3309).
 */
const trilhaDeAutomacoes = (label: string, home: string, params: URLSearchParams): ITrailStep[] => {
  const tipo = params.get("tipo");
  const tipoLabel = tipo ? TIPOS_DE_AUTOMACAO[tipo] : undefined;
  const assunto = params.get("assunto");
  const modulo = params.get("modulo");
  // O `assunto` é mais específico e vence: `?modulo=status&assunto=tax_current_account` é Conta Corrente.
  const produto = rotuloDoProduto(assunto) ?? rotuloDoProduto(modulo);

  const degraus: ITrailStep[] = [{ label, path: home }];
  if (tipoLabel) degraus.push({ label: tipoLabel, path: `${home}?tipo=${tipo}` });
  if (produto) degraus.push({ label: produto });
  // O último degrau é onde a pessoa está: não é link.
  const ultimo = degraus[degraus.length - 1];
  degraus[degraus.length - 1] = { label: ultimo.label };
  return degraus;
};

/**
 * Degraus derivados da rota, do módulo para a folha.
 *
 * `certificateName` vem do catálogo do AppContext (o mesmo que a sidebar usa para montar o
 * submenu). Quando ele não resolve — catálogo ainda carregando, ou tipo que saiu do
 * catálogo — a trilha simplesmente para no módulo, em vez de escrever o slug cru na tela.
 */
export const resolveTrail = (
  pathname: string,
  search: string,
  certificateName?: (slug: string) => string | undefined,
  /**
   * O catálogo de certidões já chegou? Sem esta informação, "ainda não sei o nome do produto" e
   * "este produto não existe" viram o mesmo caso — e a tela afirma "Visão Geral" (nenhum produto
   * escolhido) numa URL que pede um produto. Default `true` para quem não passa (testes e chamadas
   * antigas): mantém o comportamento de antes.
   */
  catalogoCarregado = true
): ITrailStep[] => {
  const modulo = MODULES.find((m) => m.match(pathname));
  if (!modulo) return [];

  const params = new URLSearchParams(search || "");

  // Certidões: o degrau da folha é o que a sidebar oferece — o PRODUTO (`?type=cnd`,
  // rotulado pelo catálogo) e/ou a jurisdição (`?jurisdiction=federal`). O `type` da URL é
  // o slug do produto, não a folha por UF: procurar `cnd_federal` no catálogo nunca acha
  // nada, e a trilha ficava presa no módulo (medido no QA de 22/08/2026).
  if (pathname.startsWith("/certidoes")) {
    const type = params.get("type");
    const jurisdiction = params.get("jurisdiction");
    const product = type && certificateName ? certificateName(type) : undefined;
    const place = jurisdiction ? JURISDICTIONS[jurisdiction.toLowerCase()] : undefined;

    // Sem nome de produto no catálogo, escrever o slug cru seria vazar o identificador
    // interno na tela (a lição da GEN-3309) — a folha cai para "Visão Geral", que é como a
    // sidebar chama a tela sem produto escolhido (e o nome que o Thiago pediu de volta).
    const label = product ? (place ? `${product} (${place})` : product) : place;
    if (label) return [{ label: modulo.label, path: modulo.home }, { label }];

    // A URL pede um produto e o catálogo ainda não chegou: a trilha para no módulo em vez de
    // afirmar "Visão Geral", que quer dizer o OPOSTO (nenhum produto escolhido). Quando o catálogo
    // chega, o degrau aparece com o nome certo.
    if (!catalogoCarregado && params.get("type")) return [{ label: modulo.label }];

    return [{ label: modulo.label, path: modulo.home }, { label: "Visão Geral" }];
  }

  if (pathname.startsWith("/automacoes")) return trilhaDeAutomacoes(modulo.label, modulo.home, params);

  // Configurações: a seção escolhida vira o degrau da folha.
  if (pathname.startsWith("/configuracoes")) {
    // GEN-3670 (r8): a seção vem do caminho (`/configuracoes/base-cadastral`) ou do `?secao=` antigo.
    const section = secaoDoSlug(pathname.split("/").filter(Boolean)[1]) ?? params.get("secao");
    const portalLabel = section ? PORTAL_SECTIONS[section] : undefined;
    if (portalLabel) {
      return [{ label: modulo.label, path: modulo.home }, { label: "Portal de autosserviço" }, { label: portalLabel }];
    }
    const label = section ? CONFIG_SECTIONS[section] : undefined;
    return label ? [{ label: modulo.label, path: modulo.home }, { label }] : [{ label: modulo.label }];
  }

  // Demais módulos com subpágina: o segundo segmento da URL.
  if (modulo.children) {
    const segment = pathname.split("/").filter(Boolean)[1];
    const label = segment ? modulo.children[segment] : undefined;
    return label ? [{ label: modulo.label, path: modulo.home }, { label }] : [{ label: modulo.label }];
  }

  return [{ label: modulo.label }];
};
